import { BaseModel, SharableModel } from "./base";
import { BackendWidget } from "./widget-v2";

// ============================================================================
// Assistant Mode Types
// ============================================================================

export type AssistantMode = "agent" | "ask" | "plan";

// ============================================================================
// Tool Call Types
// ============================================================================

export type ToolCallStatus = "pending" | "completed" | "error";

export interface ToolCall {
  id: string;
  name: string;
  args?: Record<string, unknown>;
  status: ToolCallStatus;
  result?: unknown;
  timestamp: string;
}

// ============================================================================
// Message Types
// ============================================================================

export type MessageSender = "user" | "assistant";

export interface UserQuestionOption {
  id: string;
  label: string;
}

export interface PersistableUserQuestionItem {
  question: string;
  options: UserQuestionOption[];
  allowMultiple?: boolean;
  allow_multiple?: boolean;
}

export interface PersistableUserQuestion {
  /** New multi-question format */
  questions?: PersistableUserQuestionItem[];
  /** Legacy single-question format */
  question?: string;
  options?: UserQuestionOption[];
  allowMultiple?: boolean;
  allow_multiple?: boolean;
  title?: string;
}

/**
 * API message type. The backend uses snake_case (tool_calls, pending_question)
 * but we also accept camelCase for local state compatibility.
 */
export interface Message {
  id: string;
  text: string;
  sender: MessageSender;
  timestamp: string;
  toolCalls?: ToolCall[];
  tool_calls?: ToolCall[];
  widget?: BackendWidget;
  pendingQuestion?: PersistableUserQuestion;
  pending_question?: PersistableUserQuestion;
}

/** Get tool calls from a message, handling both camelCase and snake_case. */
export function getMessageToolCalls(m: Message): ToolCall[] | undefined {
  return m.toolCalls ?? m.tool_calls;
}

/** Normalized user question for UI consumption (questions always defined). */
export interface NormalizedUserQuestion {
  questions: {
    question: string;
    options: UserQuestionOption[];
    allowMultiple?: boolean;
  }[];
  title?: string;
}

/** Get pending question from a message, normalizing to the multi-question format. */
export function getMessagePendingQuestion(
  m: Message,
): NormalizedUserQuestion | undefined {
  const q = m.pendingQuestion ?? m.pending_question;
  if (!q) return undefined;

  if (q.questions) {
    return {
      questions: q.questions.map((item) => ({
        question: item.question,
        options: item.options,
        allowMultiple: item.allowMultiple ?? item.allow_multiple,
      })),
      title: q.title,
    };
  }

  if (q.question && q.options) {
    return {
      questions: [
        {
          question: q.question,
          options: q.options,
          allowMultiple: q.allowMultiple ?? q.allow_multiple,
        },
      ],
      title: q.title,
    };
  }

  return undefined;
}

// ============================================================================
// Conversation Model
// ============================================================================

export interface Conversation extends BaseModel, SharableModel {
  name?: string | null;
  description?: string | null;
  messages: Message[];
  mode: AssistantMode;
  pinned: boolean;
  archived: boolean;
  order: number;
  icon?: string | null;
  color?: string | null;
}

export interface ConversationCreate {
  name?: string;
  description?: string;
  messages?: Message[];
  mode?: AssistantMode;
  pinned?: boolean;
  archived?: boolean;
}

export interface ConversationUpdate {
  name?: string;
  description?: string;
  messages?: Message[];
  mode?: AssistantMode;
  pinned?: boolean;
  archived?: boolean;
  order?: number;
}

// ============================================================================
// Panel State for Multi-Conversation View
// ============================================================================

export interface ConversationPanelState {
  conversationId: string;
  /** Local messages not yet saved to backend */
  localMessages?: Message[];
  hasUnsavedChanges: boolean;
}
